import { clamp, Easing } from '@/lib/anim/easing';

import { T, TIER_MARKERS } from './storyboard';

const BAR_HEIGHT = 580;

// Ch. 04 - strata: the assay column fills from the bottom while tier markers
// key in beside it.
export function Scene4Strata({ time }: { time: number }) {
  const [start, end] = T.strata;
  if (time < start || time > end) return null;
  const localTime = time - start;
  const duration = end - start;

  const inT = Easing.easeOutCubic(clamp(localTime / 0.7, 0, 1));
  const outT = clamp((localTime - (duration - 0.55)) / 0.55, 0, 1);
  const opacity = inT * (1 - outT);
  const fillProgress = Easing.easeOutCubic(clamp((localTime - 0.4) / 1.6, 0, 1)) * 0.92;

  return (
    <>
      <div
        style={{
          position: 'absolute',
          left: 950,
          top: 160,
          opacity,
          transform: `translateY(${(1 - inT) * 14}px)`,
          zIndex: 2,
        }}
      >
        <StrataBarBig fillProgress={fillProgress} />
        <div style={{ position: 'absolute', left: 120, top: 0, bottom: 0, width: 280 }}>
          {TIER_MARKERS.map((tm) => {
            const a = clamp((localTime - tm.appear) / 0.5, 0, 1);
            return (
              <div
                key={tm.label}
                style={{
                  position: 'absolute',
                  left: 0,
                  top: `${tm.y * BAR_HEIGHT}px`,
                  opacity: a,
                  transform: `translateX(${(1 - a) * 14}px)`,
                  display: 'flex',
                  alignItems: 'center',
                  gap: 12,
                }}
              >
                <span
                  style={{
                    width: 28,
                    height: 1,
                    background: tm.color,
                    boxShadow: `0 0 8px ${tm.color}`,
                  }}
                />
                <span
                  style={{
                    fontFamily: '"DM Mono", monospace',
                    fontSize: 11,
                    letterSpacing: '0.22em',
                    textTransform: 'uppercase',
                    color: tm.color,
                  }}
                >
                  {tm.label}
                </span>
              </div>
            );
          })}
        </div>
      </div>

      <div
        style={{
          position: 'absolute',
          left: 120,
          top: 290,
          width: 600,
          opacity,
          transform: `translateY(${(1 - inT) * 18}px)`,
          zIndex: 2,
        }}
      >
        <div style={{ display: 'flex', alignItems: 'center', gap: 14, marginBottom: 22 }}>
          <span style={{ width: 32, height: 1, background: 'var(--assay-ore)' }} />
          <span
            style={{
              fontFamily: '"DM Mono", monospace',
              fontSize: 11,
              letterSpacing: '0.26em',
              textTransform: 'uppercase',
              color: 'var(--assay-ore)',
            }}
          >
            Ch. 04 - Strata
          </span>
        </div>
        <h2
          style={{
            fontFamily: '"Cormorant Garamond", "Georgia", serif',
            fontWeight: 300,
            fontSize: 64,
            lineHeight: 1.05,
            color: 'var(--assay-text-primary)',
            letterSpacing: '-0.02em',
            margin: '0 0 24px',
          }}
        >
          Surface who&apos;s <em style={{ color: 'var(--assay-ore-light)' }}>due</em> first.
        </h2>
        <p
          style={{
            fontFamily: '"DM Mono", monospace',
            fontSize: 15,
            lineHeight: 1.7,
            color: 'var(--assay-text-body)',
            margin: 0,
            maxWidth: 480,
          }}
        >
          Higher trust rises to the top, every time. Like a real geological assay - your
          network&apos;s value is read in its layers.
        </p>
      </div>
    </>
  );
}

function StrataBarBig({ fillProgress }: { fillProgress: number }) {
  const STRATA = [0.15, 0.28, 0.41, 0.54, 0.67, 0.8, 0.92];
  const fill = fillProgress;
  const showGlint = fill > 0.1;
  const glintBottom = fill * 100;

  return (
    <div
      style={{
        position: 'relative',
        width: 96,
        height: BAR_HEIGHT,
        background: '#0c0906',
        border: '1px solid rgba(196,152,58,0.2)',
        borderRadius: 3,
        overflow: 'hidden',
        boxShadow: '0 0 28px rgba(196,152,58,0.18), inset 0 0 30px rgba(0,0,0,0.6)',
      }}
    >
      {STRATA.map((f) => (
        <span
          key={f}
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${f * 100}%`,
            height: 1,
            background: 'rgba(255,255,255,0.045)',
            zIndex: 3,
          }}
        />
      ))}
      <div
        style={{
          position: 'absolute',
          left: 0,
          right: 0,
          bottom: 0,
          height: `${fill * 100}%`,
          background: 'linear-gradient(to top, #3d2d0e 0%, #6b4f18 25%, #c4983a 65%, #e8b84b 100%)',
          zIndex: 2,
        }}
      />
      {showGlint && (
        <span
          style={{
            position: 'absolute',
            left: 0,
            right: 0,
            bottom: `${glintBottom}%`,
            height: 2,
            background: '#e8b84b',
            opacity: 0.9,
            zIndex: 4,
            boxShadow: '0 0 12px #e8b84b, 0 0 22px rgba(232,184,75,0.5)',
          }}
        />
      )}
    </div>
  );
}
